// MeetWaitingRoomBanner — pinned strip showing pending guest requests for
// the currently-active room. Host-only: visible when the current user is
// the room owner AND there's at least one unresolved meet_waiting_room row
// for this room in root.waitingRoom[roomId].
//
// Realtime subscription is set up in pages/meet.js via subscribeMeetRealtime
// with 'meet_waiting_room' in the tables list. The banner reads
// root.waitingRoom[roomId], filters to pending (no admitted_at, no rejected_at),
// and renders a row per guest with Admit / Reject buttons.
//
// Rendered inside VideoRoom so it appears in-call. For the list view we
// could surface a small indicator on the room row — future enhancement.

const _pendingGuests = (root, roomId) => {
  const list = root?.waitingRoom?.[roomId] || []
  return list.filter((g) => !g.admittedAt && !g.rejectedAt)
}

const _isRoomOwner = (root) => {
  const room = (root?.rooms || []).find((r) => r.id === root?.activeRoomId)
  if (!room) return false
  if (!room.createdBy) return false
  return room.createdBy === root?.currentUserId
}

export const MeetWaitingRoomBanner = {
  hide: (el, s) => {
    if (!_isRoomOwner(s.root)) return true
    return _pendingGuests(s.root, s.root?.activeRoomId).length === 0
  },
  flow: 'y',
  gap: 'V',
  padding: 'Z A',
  borderBottomWidth: '1px',
  borderBottomColor: 'line',
  borderBottomStyle: 'solid',
  background: 'overlayStrong',

  Header: {
    flow: 'x',
    align: 'center left',
    gap: 'V',
    padding: 'V 0',

    Icon: { tag: 'span', text: '👋', fontSize: 'Z2' },
    Label: {
      tag: 'span',
      fontSize: 'Y2',
      fontWeight: '600',
      letterSpacing: '.07em',
      color: 'caption',
      text: (el, s) => {
        const n = _pendingGuests(s.root, s.root?.activeRoomId).length
        return `WAITING TO JOIN · ${n}`
      }
    }
  },

  GuestList: {
    flow: 'y',
    gap: 'V',
    childrenAs: 'state',
    children: (el, s) => _pendingGuests(s.root, s.root?.activeRoomId),
    childExtends: 'MeetWaitingGuestRow'
  }
}

export const MeetWaitingGuestRow = {
  flow: 'x',
  align: 'center space-between',
  gap: 'Z1',
  padding: 'V Z',
  round: 'Z',
  background: 'document',
  borderWidth: '1px',
  borderColor: 'line',
  borderStyle: 'solid',

  Info: {
    flow: 'y',
    gap: 'V',
    flex: '1',
    minWidth: '0',

    Name: {
      tag: 'span',
      fontSize: 'Z2',
      fontWeight: '500',
      color: 'title',
      text: (el, s) => s.guestName || 'Guest'
    },
    Email: {
      hide: (el, s) => !s.guestEmail,
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      text: (el, s) => s.guestEmail
    }
  },

  Actions: {
    flow: 'x',
    gap: 'V',

    Admit: {
      tag: 'button',
      text: 'Admit',
      padding: 'Z A',
      round: 'Z',
      border: 'none',
      theme: 'primary',
      cursor: 'pointer',
      fontSize: 'Y2',
      fontWeight: '600',
      onClick: async (e, el, s) => {
        await el.call('admitGuest', s.id)
      }
    },
    Reject: {
      tag: 'button',
      text: 'Reject',
      padding: 'Z A',
      round: 'Z',
      borderWidth: '1px',
      borderColor: 'line',
      borderStyle: 'solid',
      background: 'transparent',
      color: 'caption',
      cursor: 'pointer',
      fontSize: 'Y2',
      fontWeight: '600',
      onClick: async (e, el, s) => {
        await el.call('rejectGuest', s.id)
      }
    }
  }
}
